import { FC, useState } from 'react';
import { DatagridNames, RowAction } from 'types/datagrid';
import { columns } from 'views/suppliers/PurchaseRulesTable/columns';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { PurchaseRule } from 'types/inventory';
import CreateEditPurchaseRuleDialog from 'views/suppliers/CreateEditPurchaseRuleDialog';
import DeleteConfirmationDialog from 'ui-component/DeleteConfirmationDialog';
import { ManagementActionTypes } from 'types';
import { useGetPurchaseRulesQuery } from 'store/slices/apiV1/inventory';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { appPermissionAccessLevels } from 'types/apps';
import { ALL_APPS } from 'constants/appConstants';

const PurchaseRulesTable: FC = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedPurchaseRule, setSelectedPurchaseRule] =
    useState<PurchaseRule | null>(null);

  const { data: purchaseRules } = useGetPurchaseRulesQuery({});

  const rowActions: RowAction<PurchaseRule>[] = [
    {
      Icon: IconEdit,
      getLabel: () => 'Edit',
      getRowTooltip: () => 'Edit',
      onRowClick: (row) => {
        if (row) {
          setIsEditDialogOpen(true);
          setSelectedPurchaseRule(row);
        }
      },
      showInRowMenu: true,
      permissionScope: {
        app: ALL_APPS.SUPPLIERS.id,
        accessLevel: appPermissionAccessLevels.edit,
      },
    },
    {
      Icon: IconTrash,
      getLabel: () => 'Delete',
      getRowTooltip: () => 'Delete',
      color: 'error',
      onRowClick: (row) => {
        if (row) {
          setIsDeleteDialogOpen(true);
          setSelectedPurchaseRule(row);
        }
      },
      showInRowMenu: true,
      permissionScope: {
        app: ALL_APPS.SUPPLIERS.id,
        accessLevel: appPermissionAccessLevels.edit,
      },
    },
  ];

  return (
    <>
      {selectedPurchaseRule && (
        <>
          <CreateEditPurchaseRuleDialog
            dialogOpen={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            selectedPurchaseRule={selectedPurchaseRule}
          />
          <DeleteConfirmationDialog
            idToDelete={selectedPurchaseRule.id as PurchaseRule['id']}
            dialogOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            actionType={ManagementActionTypes.purchaseRule}
          />
        </>
      )}
      <CreateEditPurchaseRuleDialog
        dialogOpen={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
      <ExtendedDatagrid
        exportFileNamePrefix="PurchaseRules"
        noRows="No purchase rules found."
        gridName={DatagridNames.purchaseRules}
        rows={purchaseRules || []}
        columns={columns}
        rowActions={rowActions}
        initialVisibleColumns={[
          'name',
          'authorizedOnly',
          'requiredCertifications',
          'allowedSuppliers',
          'preferredSuppliers',
          'isDefaultPurchaseRule',
          'actions',
        ]}
        newRecordButton={{
          label: 'New Purchase Rule',
          onClick: () => {
            setCreateDialogOpen(true);
          },
          permissionScope: {
            app: ALL_APPS.SUPPLIERS.id,
            accessLevel: appPermissionAccessLevels.edit,
          },
        }}
      />
    </>
  );
};

export default PurchaseRulesTable;
